import {
    addDays,
    addMinutes,
    addMonths,
    addSeconds,
    differenceInDays,
    differenceInHours,
    differenceInMilliseconds,
    differenceInMinutes,
    differenceInMonths,
    differenceInSeconds,
    differenceInWeeks,
    endOfDay,
    endOfWeek,
    endOfMonth,
    endOfYear,
    formatDistance,
    formatDistanceToNow,
    getDaysInMonth,
    getMonth,
    getUnixTime,
    getYear,
    isAfter,
    isBefore,
    isEqual,
    isSameDay,
    isWithinInterval,
    getQuarter,
    parseISO,
    setDate,
    setHours,
    setMinutes,
    setSeconds,
    startOfDay,
    startOfWeek,
    startOfMonth,
    startOfYear,
    subDays,
    subMinutes,
    subMonths,
    subYears,
    subSeconds,
    set,
    getDate,
} from 'date-fns';
import { format, toDate, toZonedTime } from 'date-fns-tz';
import {
    getCurrentTimezone,
    getDateFnsLocale,
    isEnglishLocale,
} from '@/value-objects/dateEnvironment.ts';

const DATE_FORMAT = 'yyyy-MM-dd';
const DATE_HUMAN_SHORT_FORMAT = 'dd MMM yyyy';
const DATE_HUMAN_SHORT_MERIDIEM_FORMAT = 'MMM dd, yyyy';
const DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXX";
const DATETIME_HUMAN_SHORT_FORMAT = 'dd MMM yyyy HH:mm';
const DATETIME_HUMAN_SHORT_MERIDIEM_FORMAT = 'MMM dd, yyyy h:mm a';
const TIME_FORMAT = 'HH:mm:ss';
const TIME_HUMAN_SHORT_FORMAT = 'HH:mm';
const TIME_HUMAN_SHORT_MERIDIEM_FORMAT = 'h:mm a';

export default class ActivixDate {
    constructor(dateTime, type = 'dateTime', timeZone = ActivixDate.timezone) {
        if (!dateTime) {
            this.dateTime = null;
            return;
        }

        if (dateTime.length === 10) {
            type = 'date';
        }

        this.type = type;

        if (dateTime instanceof Date) {
            this.dateTime = dateTime;
            return;
        }

        if (dateTime === 'now') {
            this.dateTime = new Date();
        } else if (type === 'date') {
            // Parse the string to convert it correctly
            this.dateTime = parseISO(dateTime);
        } else {
            // Convert to Date instance (We assume the given string is in UTC)
            this.dateTime = toDate(dateTime, { timeZone: 'UTC' });

            // Convert from UTC to user timezone
            this.dateTime = toZonedTime(this.dateTime, timeZone);
        }

        // Set midday to prevent date change when converting timezone
        if (type === 'date') {
            this.dateTime = setHours(this.dateTime, 12);
            this.dateTime = setMinutes(this.dateTime, 0);
            this.dateTime = setSeconds(this.dateTime, 0);
        }
    }

    static parseDate(value, name = 'dateTime') {
        if (value instanceof ActivixDate) {
            value = value.toDate();
        }

        if (!(value instanceof Date)) {
            throw new Error(`The parameter "${name}" must be a Date instance.`);
        }

        return value;
    }

    static get timezone() {
        return getCurrentTimezone();
    }

    static isValidDateString(value) {
        const regex = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}:\d{2}(?:$|Z|[+-]\d{2}:\d{2}))?$/;

        return regex.test(value);
    }

    clone() {
        return new ActivixDate(this.dateTime);
    }

    or(fallback) {
        if (this.isEmpty()) {
            return new ActivixDate(fallback);
        }

        return this.clone();
    }

    orNow() {
        return this.or('now');
    }

    startOfDay() {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(startOfDay(this.dateTime), this.type);
    }

    startOfWeek() {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(startOfWeek(this.dateTime), this.type);
    }

    startOfMonth() {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(startOfMonth(this.dateTime), this.type);
    }

    startOfYear() {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(startOfYear(this.dateTime), this.type);
    }

    endOfDay() {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(endOfDay(this.dateTime), this.type);
    }

    endOfWeek() {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(endOfWeek(this.dateTime), this.type);
    }

    endOfMonth() {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(endOfMonth(this.dateTime), this.type);
    }

    endOfYear() {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(endOfYear(this.dateTime), this.type);
    }

    addSeconds(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(addSeconds(this.dateTime, amount), this.type);
    }

    addMinutes(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(addMinutes(this.dateTime, amount), this.type);
    }

    addDays(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(addDays(this.dateTime, amount), this.type);
    }

    addMonths(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(addMonths(this.dateTime, amount), this.type);
    }

    subSeconds(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(subSeconds(this.dateTime, amount), this.type);
    }

    subMinutes(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(subMinutes(this.dateTime, amount), this.type);
    }

    subDays(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(subDays(this.dateTime, amount), this.type);
    }

    subMonths(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(subMonths(this.dateTime, amount), this.type);
    }

    subYears(amount = 1) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(subYears(this.dateTime, amount), this.type);
    }

    format(formatString, timeZone = ActivixDate.timezone, locale = '') {
        if (this.isEmpty()) {
            return '';
        }

        return format(this.dateTime, formatString, {
            locale: getDateFnsLocale(locale),
            timeZone,
        });
    }

    diffInMilliseconds(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return 0;
        }

        return differenceInMilliseconds(this.dateTime, dateTime);
    }

    diffInSeconds(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return 0;
        }

        return differenceInSeconds(this.dateTime, dateTime);
    }

    diffInMinutes(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return 0;
        }

        return differenceInMinutes(this.dateTime, dateTime);
    }

    diffInHours(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return 0;
        }

        return differenceInHours(this.dateTime, dateTime);
    }

    diffInDays(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return 0;
        }

        return differenceInDays(this.dateTime, dateTime);
    }

    diffInWeeks(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return 0;
        }

        return differenceInWeeks(this.dateTime, dateTime);
    }

    diffInMonths(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return 0;
        }

        return differenceInMonths(this.dateTime, dateTime);
    }

    formatDistanceToNow() {
        if (this.isEmpty()) {
            return '';
        }

        return formatDistanceToNow(this.dateTime, {
            locale: getDateFnsLocale(),
            addSuffix: true,
        })
            .replace(/about /g, '')
            .replace(/environ /g, '');
    }

    formatDistanceToNowInContextTimezone() {
        if (this.isEmpty()) {
            return '';
        }

        const now = new ActivixDate(new Date().toISOString());

        return formatDistance(this.dateTime, now.toDate(), {
            locale: getDateFnsLocale(),
            addSuffix: true,
        })
            .replace(/about /g, '')
            .replace(/environ /g, '');
    }

    getQuarter() {
        return getQuarter(this.month);
    }

    isEmpty() {
        return this.dateTime === null;
    }

    isNotEmpty() {
        return !this.isEmpty();
    }

    isBefore(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return false;
        }

        return isBefore(this.dateTime, dateTime);
    }

    isAfter(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return false;
        }

        return isAfter(this.dateTime, dateTime);
    }

    isBetween(start, end) {
        if (!start || !end) {
            return false;
        }

        if (
            (start instanceof ActivixDate && start.isEmpty()) ||
            (end instanceof ActivixDate && end.isEmpty())
        ) {
            return false;
        }

        start = ActivixDate.parseDate(start, 'start');
        end = ActivixDate.parseDate(end, 'end');

        if (this.isEmpty()) {
            return false;
        }

        return isWithinInterval(this.dateTime, { start, end });
    }

    isSame(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return false;
        }

        return isEqual(this.dateTime, dateTime);
    }

    isSameDay(dateTime) {
        dateTime = ActivixDate.parseDate(dateTime);

        if (this.isEmpty()) {
            return false;
        }

        return isSameDay(this.dateTime, dateTime);
    }

    isSameOrAfter(dateTime) {
        return this.isSame(dateTime) || this.isAfter(dateTime);
    }

    isSameOrBefore(dateTime) {
        return this.isSame(dateTime) || this.isBefore(dateTime);
    }

    isToday() {
        return this.isSameDay(new Date());
    }

    isPast() {
        return this.isBefore(new Date());
    }

    isFuture() {
        return this.isAfter(new Date());
    }

    isStartOfDay() {
        if (this.isEmpty()) {
            return false;
        }

        return this.toTimeString() == '00:00:00';
    }

    isEndOfDay() {
        if (this.isEmpty()) {
            return false;
        }

        return this.toTimeString() == '23:59:59';
    }

    toDate() {
        return this.dateTime;
    }

    toHumanShort(withTime = true, locale = '') {
        let format = isEnglishLocale(locale)
            ? DATE_HUMAN_SHORT_MERIDIEM_FORMAT
            : DATE_HUMAN_SHORT_FORMAT;

        if (this.type === 'dateTime' && withTime) {
            format = isEnglishLocale(locale)
                ? DATETIME_HUMAN_SHORT_MERIDIEM_FORMAT
                : DATETIME_HUMAN_SHORT_FORMAT;
        }

        return this.format(format, ActivixDate.timezone, locale);
    }

    toHumanTime() {
        const format = isEnglishLocale()
            ? TIME_HUMAN_SHORT_MERIDIEM_FORMAT
            : TIME_HUMAN_SHORT_FORMAT;

        return this.format(format);
    }

    toTimeString() {
        return this.format(TIME_FORMAT);
    }

    toDateString() {
        return this.format(DATE_FORMAT);
    }

    toDateTimeString() {
        return this.format(DATETIME_FORMAT);
    }

    toString() {
        if (this.type === 'time') {
            return this.toTimeString();
        }

        if (this.type === 'date') {
            return this.toDateString();
        }

        return this.toDateTimeString();
    }

    setSeconds(seconds) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(setSeconds(this.dateTime, seconds), this.type);
    }

    setMinutes(minutes) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(setMinutes(this.dateTime, minutes), this.type);
    }

    setHours(hours) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(setHours(this.dateTime, hours), this.type);
    }

    setDate(date) {
        if (this.isEmpty()) {
            return this;
        }

        return new ActivixDate(setDate(this.dateTime, date), this.type);
    }

    setFullDate(date) {
        if (this.isEmpty()) {
            return this;
        }

        const newDate = new ActivixDate(date);

        return new ActivixDate(
            set(this.dateTime, {
                year: newDate.year,
                month: newDate.month,
                date: newDate.date,
            }),
            this.type
        );
    }

    get date() {
        if (this.isEmpty()) {
            return null;
        }

        return getDate(this.dateTime);
    }

    get month() {
        if (this.isEmpty()) {
            return null;
        }

        return getMonth(this.dateTime);
    }

    get year() {
        if (this.isEmpty()) {
            return null;
        }

        return getYear(this.dateTime);
    }

    get hours() {
        if (this.isEmpty()) {
            return null;
        }

        return this.dateTime.getHours();
    }

    get minutes() {
        if (this.isEmpty()) {
            return null;
        }

        return this.dateTime.getMinutes();
    }

    get timestamp() {
        if (this.isEmpty()) {
            return null;
        }

        return getUnixTime(this.dateTime);
    }

    get daysInMonth() {
        if (this.isEmpty()) {
            return null;
        }

        return getDaysInMonth(this.dateTime);
    }

    static get DATE_HUMAN_SHORT_FORMAT() {
        return DATE_HUMAN_SHORT_FORMAT;
    }

    static get DATETIME_FORMAT() {
        return DATETIME_FORMAT;
    }
}
